<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useId, watch, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import AppButton from '../AppButton/AppButton.vue'
import AppScrim from '../AppScrim/AppScrim.vue'
import AppScrollArea from '../AppScrollArea/AppScrollArea.vue'
import { useFocusTrap } from '../../../composables/useFocusTrap'
import { useReturnFocus } from '../../../composables/useReturnFocus'
import { useScrollLock } from '../../../composables/useScrollLock'
import type { DialogRole, DialogWidth } from './dialog-types'

const open = defineModel<boolean>('open', { default: false })

const props = withDefaults(
  defineProps<{
    /** Heading - required, wired as the `aria-labelledby` target. */
    title: string
    /** Optional supporting copy, wired as the `aria-describedby` target. */
    description?: string
    /** `dialog` for a form the user fills in, `alertdialog` for a decision that interrupts them. */
    role?: DialogRole
    /** Panel measure: `sm` for a confirmation, `md` for a short form, `viewer` for a preview panel. */
    width?: DialogWidth
    /** Allow Escape / overlay click to dismiss. Off by default (a11y). */
    dismissible?: boolean
    /** Hands the whole panel to the default slot: no padded header, no actions row; the title still names the dialog for assistive tech. */
    bleed?: boolean
    /** Label of the confirm button; the actions row renders only when this is set. */
    confirmLabel?: string
    /** Confirm button tone: `primary` (default) or `destructive` for an action that cannot be undone. */
    confirmVariant?: 'primary' | 'destructive'
    /** Work is in flight: confirm shows its spinner and Cancel goes inactive. */
    confirmLoading?: boolean
    /** Announced on the confirm button while `confirmLoading`; defaults to its label. */
    confirmLoadingLabel?: string
    /** Confirm cannot be pressed yet, for example while the form is incomplete. */
    confirmInactive?: boolean
    /** Trailing icon on the confirm button. */
    confirmEndIcon?: Component | null
    /** Label of the Cancel button; defaults to the shared "Cancel" copy. */
    cancelLabel?: string
    /** Drops Cancel for a single-action dialog. */
    hideCancel?: boolean
    /** Cancel closes the dialog (default); turn off when Cancel steps back inside the dialog and the caller keeps it open. */
    cancelCloses?: boolean
    /** Label of a destructive button at the start of the actions row, such as "Remove topic"; omit for none. */
    destructiveLabel?: string
    /** The destructive work is in flight: its button shows a spinner and the other actions go inactive. */
    destructiveLoading?: boolean
    /** The destructive button cannot be pressed yet. */
    destructiveInactive?: boolean
    /** Runs before Cancel, Escape or an overlay click closes the dialog; resolve `false` to keep it open. */
    closeGuard?: () => boolean | Promise<boolean>
    /** Focuses the first action on open instead of the first control in the body - for a dialog the user reads rather than fills in. */
    focusActions?: boolean
  }>(),
  {
    description: undefined,
    role: 'dialog',
    width: 'sm',
    dismissible: false,
    bleed: false,
    confirmLabel: undefined,
    confirmVariant: 'primary',
    confirmLoading: false,
    confirmLoadingLabel: undefined,
    confirmInactive: false,
    confirmEndIcon: null,
    cancelLabel: undefined,
    hideCancel: false,
    cancelCloses: true,
    destructiveLabel: undefined,
    destructiveLoading: false,
    destructiveInactive: false,
    closeGuard: undefined,
    focusActions: false,
  },
)

const emit = defineEmits<{
  /** The dialog was dismissed - by Escape, by the overlay, or by a caller closing it. */
  dismiss: []
  /** Cancel was pressed; the dialog has already closed itself unless `cancelCloses` is off. */
  cancel: []
  /** Confirm was pressed; the dialog stays open, so the caller closes it once its work lands. */
  confirm: []
  /** The destructive button was pressed; the dialog stays open, so the caller closes it once its work lands. */
  destructive: []
}>()

defineSlots<{
  eyebrow?: () => unknown
  note?: () => unknown
  default?: () => unknown
  /** Status that sits at the start of the actions row, opposite the buttons (a spinner or a hint); never a button. */
  'actions-start'?: () => unknown
}>()

const { t } = useI18n()
const resolvedCancelLabel = computed(() => props.cancelLabel ?? t('common.cancel'))

const titleId = useId()
const descId = useId()

const panelRef = ref<HTMLElement | null>(null)
const actionsRef = ref<HTMLElement | null>(null)
const trap = useFocusTrap(panelRef)
const scrollLock = useScrollLock()
const returnFocus = useReturnFocus()

let hasScrollLock = false

async function mayClose(): Promise<boolean> {
  return props.closeGuard ? await props.closeGuard() : true
}

async function dismiss(): Promise<void> {
  if (!props.dismissible || !(await mayClose())) return
  emit('dismiss')
  open.value = false
}

async function onCancel(): Promise<void> {
  if (!(await mayClose())) return
  emit('cancel')
  if (props.cancelCloses) open.value = false
}

const busy = computed(() => props.confirmLoading || props.destructiveLoading)

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return
  if (!trap.isTopmost()) return
  event.preventDefault()
  dismiss()
}

watch(
  open,
  async (isOpen) => {
    if (typeof document === 'undefined') return
    if (isOpen) {
      returnFocus.capture()
      document.addEventListener('keydown', onKeydown)
      trap.activate()
      scrollLock.lock()
      hasScrollLock = true
      await nextTick()
      const action = props.focusActions
        ? trap.focusables().find((element) => actionsRef.value?.contains(element))
        : undefined
      const chosen = panelRef.value?.querySelector<HTMLElement>('[autofocus]')
      if (action) action.focus()
      else if (chosen) chosen.focus()
      else trap.focusFirstTabbable()
    } else {
      document.removeEventListener('keydown', onKeydown)
      trap.deactivate()
      if (hasScrollLock) {
        scrollLock.unlock()
        hasScrollLock = false
      }
      returnFocus.restore()
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (typeof document === 'undefined') return
  document.removeEventListener('keydown', onKeydown)
  trap.deactivate()
  if (hasScrollLock) {
    scrollLock.unlock()
    hasScrollLock = false
    returnFocus.restore()
  }
})

const WIDTH_CLASSES: Record<DialogWidth, string> = {
  sm: 'max-w-(--container-dialog-sm)',
  md: 'max-w-(--container-dialog-md)',
  viewer: 'max-w-(--container-dialog-viewer)',
}
</script>

<template>
  <Teleport to="body">
    <AppScrim :open="open" />
    <Transition enter-active-class="animate-dialog-in" leave-active-class="animate-dialog-out">
      <div
        v-if="open"
        class="fixed inset-0 z-50 flex items-center justify-center p-16"
        @click.self="dismiss"
      >
        <div
          ref="panelRef"
          :role="role"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="description ? descId : undefined"
          tabindex="-1"
          :class="[
            'relative z-50 mx-auto flex max-h-full w-full flex-col rounded-4 border border-border bg-bg-primary shadow-3 outline-none',
            WIDTH_CLASSES[width],
          ]"
        >
          <template v-if="bleed">
            <h2 :id="titleId" class="sr-only">{{ title }}</h2>
            <p v-if="description" :id="descId" class="sr-only">{{ description }}</p>
            <slot />
          </template>

          <template v-else>
            <div class="flex flex-col gap-4 p-16 pb-0 text-center sm:text-left">
              <slot name="eyebrow" />
              <h2 :id="titleId" class="text-h3 text-text-primary">{{ title }}</h2>
              <p v-if="description" :id="descId" class="text-body-sm text-text-secondary">
                {{ description }}
              </p>
              <slot name="note" />
            </div>

            <AppScrollArea v-if="$slots.default" class="min-h-0 flex-1 p-16">
              <slot />
            </AppScrollArea>

            <div
              v-if="confirmLabel"
              ref="actionsRef"
              class="flex flex-col-reverse gap-8 p-16 pt-0 sm:flex-row sm:justify-end"
            >
              <div v-if="$slots['actions-start']" class="flex items-center sm:mr-auto">
                <slot name="actions-start" />
              </div>
              <div v-if="destructiveLabel" class="flex sm:mr-auto">
                <AppButton
                  variant="sec-destructive"
                  :label="destructiveLabel"
                  :loading="destructiveLoading"
                  :is-inactive="busy || destructiveInactive"
                  @click="emit('destructive')"
                />
              </div>
              <AppButton
                v-if="!hideCancel"
                variant="ghost"
                :label="resolvedCancelLabel"
                :is-inactive="busy"
                @click="onCancel"
              />
              <AppButton
                :variant="confirmVariant"
                :label="confirmLabel"
                :loading="confirmLoading"
                :loading-label="confirmLoadingLabel"
                :is-inactive="confirmInactive || destructiveLoading"
                :end-icon="confirmEndIcon"
                @click="emit('confirm')"
              />
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
